import { formatUsd } from "@haulsheet/core";
import type { DeadLetterRow } from "../db/types";

/**
 * Plain-language explanations for rejected sync changes (dead letters).
 * `canRetry`: sending the same change again could work (a transient server
 * problem, or something the driver can fix elsewhere first). When false,
 * the only sensible action is Discard.
 */
export interface Explained {
  text: string;
  canRetry: boolean;
}

const CONSTRAINTS: Record<string, string> = {
  detention_departed_after_arrived: "The detention end time has to be after the start time.",
};

// Postgres SQLSTATE classes that are worth retrying as-is.
const TRANSIENT = /^db_(40001|40P01|55P03|57014|53\d{3}|08\d{3})$/;

export function explainRejection(reason: string | undefined | null): Explained {
  const r = (reason ?? "").trim();
  if (r === "held_behind_failed_create") {
    return { text: "Saved on this phone, waiting for the new item it belongs to. Retry sends both.", canRetry: true };
  }
  if (r === "not_found") {
    return { text: "This was deleted on another device or by your fleet, so the change had nowhere to go.", canRetry: false };
  }
  if (r === "row_exists") {
    return { text: "The server already has this item, so it wasn't saved a second time.", canRetry: false };
  }
  if (r.startsWith("FORBIDDEN")) {
    return { text: "This belongs to another driver, so your account can't change it.", canRetry: false };
  }
  if (r === "money_locked") {
    return { text: "Pay on a delivered or paid load can only be changed by the owner.", canRetry: false };
  }
  if (r.startsWith("USAGE_LIMIT")) {
    return { text: "Your plan's active-load limit is reached. Finish or delete a load (or upgrade), then retry.", canRetry: true };
  }
  if (r === "storage_key_forbidden") {
    return { text: "The document link was invalid.", canRetry: false };
  }
  if (TRANSIENT.test(r)) {
    return { text: "The server was busy and couldn't save this. Try again.", canRetry: true };
  }
  if (CONSTRAINTS[r]) return { text: CONSTRAINTS[r]!, canRetry: false };
  if (/^invalid_/.test(r) || /_check$|_fkey$|_key$/.test(r) || /^db_/.test(r)) {
    return { text: "The server's data check refused this change.", canRetry: false };
  }
  // canTransition rejections are "CODE: human message".
  const m = /^[A-Z_]+: (.+)$/.exec(r);
  if (m) return { text: m[1]!, canRetry: true };
  return { text: r ? `The server refused this change (${r}).` : "The server refused this change.", canRetry: true };
}

export interface FailedGroup {
  key: string;
  entity: DeadLetterRow["entity"];
  id: string;
  rows: DeadLetterRow[];
  /** Newest rejection. */
  latest: DeadLetterRow;
  kind: "new" | "edit" | "delete";
}

/** One entry per record; several rejected changes to the same row show once. */
export function groupFailed(rows: ReadonlyArray<DeadLetterRow>): FailedGroup[] {
  const by = new Map<string, DeadLetterRow[]>();
  for (const r of rows) {
    const k = `${r.entity}:${r.id}`;
    by.set(k, [...(by.get(k) ?? []), r]);
  }
  const out: FailedGroup[] = [];
  for (const [key, list] of by) {
    const sorted = [...list].sort((a, b) => a.createdAt.localeCompare(b.createdAt));
    // The server's own answer explains the group better than a parked follow-up.
    const byTime = [...list].sort((a, b) => a.at.localeCompare(b.at));
    const real = byTime.filter((r) => r.reason !== "held_behind_failed_create");
    const latest = (real.length ? real : byTime).at(-1)!;
    const last = sorted.at(-1)!;
    const kind = last.op === "delete" ? "delete" : sorted.some((r) => r.baseVersion == null) ? "new" : "edit";
    out.push({ key, entity: last.entity, id: last.id, rows: sorted, latest, kind });
  }
  return out.sort((a, b) => b.latest.at.localeCompare(a.latest.at));
}

const ENTITY_LABEL: Record<string, string> = {
  load: "Load",
  load_stop: "Load stop",
  load_event: "Load event",
  expense: "Expense",
  detention: "Detention",
  document: "Document",
  pay_contract: "Pay contract",
  cost_profile: "Costs",
  truck: "Truck",
  trailer: "Trailer",
  driver: "Driver",
  broker: "Broker",
  broker_contact: "Broker contact",
};

type Rec = Record<string, unknown>;
const str = (v: unknown) => (typeof v === "string" && v.trim() ? v.trim() : null);
const num = (v: unknown) => (typeof v === "number" && Number.isFinite(v) ? v : null);

/** "Fuel · 120 gal · $535.08 · 2026-10-07" from the local row merged with the change. */
export function describeRecord(entity: string, data: Rec): string {
  if (entity === "expense") {
    const fuel = data["kind"] === "fuel";
    const parts = [fuel ? "Fuel" : `Expense${str(data["kind"]) ? ` (${String(data["kind"])})` : ""}`];
    if (num(data["gallons"]) != null) parts.push(`${num(data["gallons"])} gal`);
    if (num(data["amountCents"]) != null) parts.push(formatUsd(num(data["amountCents"])!));
    if (str(data["occurredOn"])) parts.push(String(data["occurredOn"]).slice(0, 10));
    if (str(data["note"])) parts.push(String(data["note"]));
    return parts.join(" · ");
  }
  if (entity === "detention") {
    const parts = ["Detention"];
    if (str(data["placeText"])) parts.push(String(data["placeText"]));
    if (num(data["payCents"]) != null) parts.push(formatUsd(num(data["payCents"])!));
    return parts.join(" · ");
  }
  if (entity === "load") {
    const o = (data["origin"] as Rec | undefined)?.["text"];
    const d = (data["destination"] as Rec | undefined)?.["text"];
    const parts = ["Load"];
    if (str(o) || str(d)) parts.push(`${str(o) ?? "?"} → ${str(d) ?? "?"}`);
    if (str(data["refNumber"])) parts.push(`#${String(data["refNumber"])}`);
    return parts.join(" · ");
  }
  const label = ENTITY_LABEL[entity] ?? entity;
  const name = str(data["name"]) ?? str(data["unitNumber"]) ?? str(data["placeText"]);
  return name ? `${label} · ${name}` : label;
}
